import type { NavIconKey } from './NavIcons';
import type { BevelTone } from './BevelButton';

/**
 * Sidebar item registry — everything user-facing about each nav item
 * lives here, including the one-liner blurb that the BevelButton's
 * projected popover renders on hover. Keeping it data-driven keeps
 * Sidebar.tsx focused on layout.
 *
 * Editing copy: just change `blurb`. Editing tones: pick from
 * BevelTone. Both are independent of routing.
 */

export interface SidebarItem {
  id: NavIconKey;
  label: string;
  /** One-liner shown in the projected popover. Should be a complete
   *  sentence under ~70 chars. */
  blurb: string;
  /** Tone used for hover glow + active state. */
  tone: BevelTone;
  /** Pathname OR pathname#hash. Click navigates here. */
  route: string;
  /** Hide for non-platform-admin users. */
  adminOnly?: boolean;
  /** Hide when the data_pane_v2 feature flag is on (legacy item). */
  hideWhenDataPaneV2?: boolean;
}

/* ─── Category 1 — Core (always expanded) ───────────────────────── */

export const CORE_ITEMS: readonly SidebarItem[] = [
  {
    id: 'zilm',
    label: 'ZILM',
    blurb: 'Ask the AI strategy assistant — turn data into decisions',
    tone: 'cyan',
    route: '/zilm',
  },
  {
    id: 'funding',
    label: 'Funding',
    blurb: 'Track grant pipelines + match programs to your portfolio',
    tone: 'gold',
    route: '/funding',
  },
  {
    id: 'compliance',
    label: 'Compliance',
    blurb: 'FHWA + state regulatory rules, checklists, deadlines',
    tone: 'lime',
    route: '/compliance',
  },
  {
    id: 'analytics',
    label: 'Data & Analytics',
    blurb: 'Cross-state KPIs, trend explorer, peer benchmarks',
    tone: 'cyan',
    route: '/analytics',
  },
  {
    id: 'workflows',
    label: 'Workflows',
    blurb: 'Automate inspections, permits, and reviews',
    tone: 'violet',
    route: '/workflows',
  },
  {
    id: 'briefs',
    label: 'Briefs',
    blurb: 'AI-generated executive summaries from your data',
    tone: 'amber',
    route: '/briefs',
  },
];

/* ─── Category 3 — Advanced (always expanded) ───────────────────── */

export const ADVANCED_ITEMS: readonly SidebarItem[] = [
  {
    id: 'admin',
    label: 'Admin',
    blurb: 'Org settings, credits, team management',
    tone: 'rose',
    route: '/admin',
    adminOnly: true,
  },
  {
    id: 'data-pane',
    label: 'Data',
    blurb: 'Catalog of every feed flowing into the medallion',
    tone: 'cyan',
    route: '/data-pane',
  },
  {
    id: 'user',
    label: 'User',
    blurb: 'Profile, preferences, sessions',
    tone: 'violet',
    route: '/settings',
  },
  {
    id: 'tools',
    label: 'Tools',
    blurb: 'Mid-task calculators + planners',
    tone: 'amber',
    route: '/tools',
  },
  {
    id: 'labs',
    label: 'Labs',
    blurb: 'Try the experimental AI agents',
    tone: 'lime',
    route: '/labs',
  },
  {
    id: 'whatsnew',
    label: "What's New",
    blurb: 'Recent releases + roadmap',
    tone: 'gold',
    route: '/whatsnew',
  },
  {
    id: 'feedback',
    label: 'Feedback',
    blurb: 'Review user feedback (admin only)',
    tone: 'rose',
    route: '/admin#feedback',
    adminOnly: true,
  },
  {
    id: 'data-explorer',
    label: 'Lineage',
    blurb: 'Data lineage + freshness explorer (admin only)',
    tone: 'cyan',
    route: '/data-explorer',
    adminOnly: true,
    hideWhenDataPaneV2: true,
  },
];

/* ─── Right rail — Tools (always expanded) ──────────────────────── */

export interface ToolsItem {
  id: string;
  /** Emoji or single-character glyph rendered in the bevel button. */
  glyph: string;
  label: string;
  blurb: string;
  tone: BevelTone;
  route?: string;
  /** Special action handlers (e.g. open feedback drawer). */
  action?: 'feedback';
}

export const TOOLS_ITEMS: readonly ToolsItem[] = [
  {
    id: 'all-tools',
    glyph: '▦',
    label: 'All Tools',
    blurb: 'Browse the full library of calculators + planners',
    tone: 'cyan',
    route: '/tools/all-tools',
  },
  {
    id: 'calculator',
    glyph: '🧮',
    label: 'Calculator',
    blurb: 'Quick budget + funding + obligation math',
    tone: 'amber',
    route: '/tools/calculator',
  },
  {
    id: 'assessment',
    glyph: '✓',
    label: 'Assessment',
    blurb: 'Score project readiness + risk',
    tone: 'lime',
    route: '/tools/assessment',
  },
  {
    id: 'workflow',
    glyph: '⟳',
    label: 'Workflow',
    blurb: 'Run a guided workflow end-to-end',
    tone: 'violet',
    route: '/tools/workflow',
  },
  {
    id: 'intel-brief',
    glyph: '📄',
    label: 'Intel Brief',
    blurb: 'Generate a brief from the latest signals',
    tone: 'amber',
    route: '/tools/intel-brief',
  },
  {
    id: 'intel-feed',
    glyph: '📡',
    label: 'Intel Feed',
    blurb: 'Live stream of regulatory + market intel',
    tone: 'cyan',
    route: '/tools/intel-feed',
  },
  {
    id: 'scenarios',
    glyph: '🔀',
    label: 'Scenarios',
    blurb: 'What-if modeling for plans + budgets',
    tone: 'gold',
    route: '/tools/scenarios',
  },
  {
    id: 'gis-view',
    glyph: '🗺',
    label: 'GIS View',
    blurb: 'Open the geospatial workspace',
    tone: 'lime',
    route: '/tools/gis-view',
  },
];

export const INFO_ITEMS: readonly ToolsItem[] = [
  {
    id: 'feedback',
    glyph: '💬',
    label: 'Feedback',
    blurb: 'Send a quick note to the Zoneium team',
    tone: 'rose',
    action: 'feedback',
  },
  {
    id: 'partners',
    glyph: '🤝',
    label: 'Partners',
    blurb: 'Partner directory + integrations',
    tone: 'gold',
    route: '/admin#partners',
  },
  {
    id: 'upgrade',
    glyph: '⬆',
    label: 'Upgrade',
    blurb: 'Upgrade your plan or add credits',
    tone: 'amber',
    route: '/admin#upgrade',
  },
];

export const HELP_ITEM: ToolsItem = {
  id: 'help',
  glyph: '?',
  label: 'Help',
  blurb: 'Docs, shortcuts, contact support',
  tone: 'cyan',
  route: '/whatsnew',
};
